// react
import React, { useState, useEffect } from "react";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Typography, Card, CardContent, CircularProgress, useTheme } from "@mui/material";

// features/user/components
import UserIdentityInfo from "features/user/components/UserIdentityInfo";
import DataKeysTable from "features/user/components/DataKeysTable";
import MnemonicDialog from "features/user/components/MnemonicDialog";
import SigningKeysCard from "features/user/components/SigningKeysCard";
import CreateDataKeyDialog from "features/user/components/CreateDataKeyDialog";
import PasswordPromptDialog from "features/user/components/PasswordPromptDialog";
import MnemonicActionDialog from "features/user/components/MnemonicActionDialog";

// components
import { RemovedKeySnackbar, ImportedKeySnackbar, NewDataKeySuccessful, NewDataKeyError } from "components/app/AppFeedback";

// firebase
import { db } from "config/firebase";
import { onSnapshot, query, where, collection } from "firebase/firestore";

// lib/safetwin
import { seedFlow } from "lib/safetwin/3S/seedFlow";
import { forgetMnemonic } from "lib/safetwin/3S/forgetMnemonic";
import { loadMnemonic } from "lib/safetwin/3S/loadMnemonic";
import { getMnemonic } from "lib/safetwin/3S/getMnemonic";
import { generateMnemonic } from "lib/safetwin/crypto/bip39/bip39";
import { genRndStringPrintable } from "lib/safetwin/crypto/cryptolibsodium";

// features/datakey
import getDataKeysByOwner from "features/datakey/getDataKeysByOwner";

// A ---------------------------------------------------------------------- M

const AccountSecurity = () => {
  const { user, setSeedFlow, checkPassword, setPubKey, conservSostL1 } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();

  const [dataKeys, setDataKeys] = useState([]);
  const [dataKey, setDataKey] = useState("");
  const [passwordExport, setPasswordExport] = useState("");
  const [passwordForget, setPasswordForget] = useState("");
  const [passwordGenerate, setPasswordGenerate] = useState("");
  const [passwordImport, setPasswordImport] = useState("");
  const [mnemonicExport, setMnemonicExport] = useState("");
  const [mnemonicImport, setMnemonicImport] = useState("");
  const [mnemonicGenerate, setMnemonicGenerate] = useState("");
  const [openForget, setOpenForget] = useState(false);
  const [openPasswordImport, setOpenPasswordImport] = useState(false);
  const [openPasswordExport, setOpenPasswordExport] = useState(false);
  const [openMnemonicForm, setOpenMnemonicForm] = useState(false);
  const [openMnemonicExport, setOpenMnemonicExport] = useState(false);
  const [openPasswordGenerate, setOpenPasswordGenerate] = useState(false);
  const [openMnemonicGenerate, setOpenMnemonicGenerate] = useState(false);
  const [isDataKeyDialogOpen, setDataKeyDialogOpen] = useState(false);
  const [openDataKeySnackbarSuccessful, setOpenDataKeySnackbarSuccessful] = useState(false);
  const [openDataKeySnackbarError, setOpenDataKeySnackbarError] = useState(false);
  const [openRemovedSecretSnackbarSuccessful, setOpenRemovedSecretSnackbarSuccessful] = useState(false);
  const [openImportedSecretSnackbarSuccessful, setOpenImportedSecretSnackbarSuccessful] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handlePopstate = () => {
      if (openPasswordExport) handleClosePasswordExport();
      if (openMnemonicExport) handleCloseMnemonicExport();
      if (openForget) handleCloseForget();
      if (openPasswordImport) handleClosePasswordImport();
      if (openMnemonicForm) handleCloseMnemonicForm();
      if (openPasswordGenerate) handleClosePasswordGenerate();
      if (openMnemonicGenerate) handleCloseMnemonicGenerate();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [openPasswordExport, openMnemonicExport, openForget, openPasswordImport, openMnemonicForm, openPasswordGenerate, openMnemonicGenerate]);

  useEffect(() => {
    const fetchDataKeys = async () => {
      if (user) {
        const keys = await getDataKeysByOwner(user.uid);
        keys.sort((a, b) => b.created_on - a.created_on);
        setDataKeys(keys);
      }
    };

    const loadData = async () => {
      setIsLoading(true);
      await fetchDataKeys();
      setIsLoading(false);
    };

    loadData();

    const currentTime = Math.floor(Date.now() / 1000);
    const keysQuery = query(collection(db, "keydata"), where("owner_uuid", "==", user.uid), where("created_on", ">=", currentTime.toString()));
    const unsubscribe = onSnapshot(keysQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") fetchDataKeys();
      });
    });

    return () => unsubscribe();
  }, [user]);

  const handleOpenPasswordExport = () => {
    setOpenPasswordExport(true);
    window.history.pushState(null, "");
  };

  const handleExportKey = async () => {
    const check = await checkPassword(passwordExport);
    if (check) {
      setInvalidPassword(false);
      setOpenPasswordExport(false);
      handleOpenMnemonicExport();
      const mnemonic = await getMnemonic(user, passwordExport);
      setMnemonicExport(mnemonic);
      setPasswordExport("");
    } else {
      setPasswordExport("");
      setInvalidPassword(true);
    }
  };

  const handleClosePasswordExport = () => {
    setPasswordExport("");
    setInvalidPassword(false);
    setOpenPasswordExport(false);
  };

  const handleOpenForget = () => {
    setOpenForget(true);
    window.history.pushState(null, "");
  };

  const handleForgetKey = async () => {
    const check = await checkPassword(passwordForget);
    if (check) {
      setInvalidPassword(false);
      setLoading(true);
      const public_key = await forgetMnemonic(user);
      if (public_key) setPubKey("");
      setLoading(false);
      setSeedFlow(false);
      setPasswordForget("");
      setOpenForget(false);
      setOpenRemovedSecretSnackbarSuccessful(true);
    } else {
      setPasswordForget("");
      setInvalidPassword(true);
    }
  };

  const handleCloseForget = () => {
    setPasswordForget("");
    setInvalidPassword(false);
    setOpenForget(false);
  };

  const handleOpenPasswordGenerate = () => {
    setOpenPasswordGenerate(true);
    window.history.pushState(null, "");
  };

  const handleGenerateKey = async () => {
    const check = await checkPassword(passwordGenerate);
    if (check) {
      setInvalidPassword(false);
      setOpenPasswordGenerate(false);
      handleOpenMnemonicGenerate();
      const mnemonic = generateMnemonic();
      setMnemonicGenerate(mnemonic);
    } else {
      setPasswordGenerate("");
      setInvalidPassword(true);
    }
  };

  const handleClosePasswordGenerate = () => {
    setPasswordGenerate("");
    setInvalidPassword(false);
    setOpenPasswordGenerate(false);
  };

  const handleOpenPasswordImport = () => {
    setOpenPasswordImport(true);
    window.history.pushState(null, "");
  };

  const handleClosePasswordImport = () => {
    setPasswordImport("");
    setInvalidPassword(false);
    setOpenPasswordImport(false);
  };

  const handleOpenMnemonicForm = async () => {
    const check = await checkPassword(passwordImport);
    if (check) {
      setInvalidPassword(false);
      setOpenPasswordImport(false);
      handleOpenMnemonicFormDrawer();
    } else {
      setPasswordImport("");
      setInvalidPassword(true);
    }
  };

  const handleOpenMnemonicFormDrawer = () => {
    setOpenMnemonicForm(true);
    window.history.pushState(null, "");
  };

  const handleLoadMnemonic = async () => {
    setLoading(true);
    const public_key = await loadMnemonic(user, passwordImport, mnemonicImport);
    if (public_key) setPubKey(public_key);
    const outcome = await seedFlow(user, passwordImport);
    setSeedFlow(outcome);
    setLoading(false);
    setOpenImportedSecretSnackbarSuccessful(true);
    handleCloseMnemonicForm();
  };

  const handleCloseMnemonicForm = () => {
    setPasswordImport("");
    setMnemonicImport("");
    setOpenMnemonicForm(false);
  };

  const handleOpenMnemonicExport = () => {
    setOpenMnemonicExport(true);
    window.history.pushState(null, "");
  };

  const handleCloseMnemonicExport = () => {
    setMnemonicExport("");
    setCopied(false);
    setOpenMnemonicExport(false);
  };

  const handleOpenMnemonicGenerate = () => {
    setOpenMnemonicGenerate(true);
    window.history.pushState(null, "");
  };

  const handleLoadMnemonicGenerate = async () => {
    setLoading(true);
    const public_key = await loadMnemonic(user, passwordGenerate, mnemonicGenerate);
    if (public_key) setPubKey(public_key);
    const outcome = await seedFlow(user, passwordGenerate);
    setSeedFlow(outcome);
    setLoading(false);
    setOpenImportedSecretSnackbarSuccessful(true);
    handleCloseMnemonicGenerate();
  };

  const handleCloseMnemonicGenerate = () => {
    setPasswordGenerate("");
    setMnemonicGenerate("");
    setOpenMnemonicGenerate(false);
  };

  const handleCopySuccess = () => {
    setCopied(true);
  };

  const handleCloseDataKeyDialog = () => {
    setDataKeyDialogOpen(false);
  };

  const handleOpenDataKeyDialog = () => {
    const dataKey = genRndStringPrintable(32);
    setDataKey(dataKey);
    setDataKeyDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center" mt="30%">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" fontWeight={700}>
          {t("my_account")}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default}F2)`,
            border: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            transition: "all 0.3s ease",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <UserIdentityInfo />
          </CardContent>
        </Card>
      </Grid>

      {!conservSostL1 && (
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 4,
              background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default}F2)`,
              border: `1px solid ${theme.palette.divider}`,
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <SigningKeysCard
                handleOpenPasswordExport={handleOpenPasswordExport}
                handleOpenForget={handleOpenForget}
                handleOpenPasswordGenerate={handleOpenPasswordGenerate}
                handleOpenPasswordImport={handleOpenPasswordImport}
              />
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid item xs={12}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default}F2)`,
            border: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <DataKeysTable dataKeys={dataKeys} handleOpen={handleOpenDataKeyDialog} />
          </CardContent>
        </Card>
      </Grid>

      <CreateDataKeyDialog
        dataKey={dataKey}
        open={isDataKeyDialogOpen}
        handleClose={handleCloseDataKeyDialog}
        setOpenDataKeySnackbarSuccessful={setOpenDataKeySnackbarSuccessful}
        setOpenDataKeySnackbarError={setOpenDataKeySnackbarError}
      />

      <NewDataKeySuccessful open={openDataKeySnackbarSuccessful} setOpen={setOpenDataKeySnackbarSuccessful} />

      <NewDataKeyError open={openDataKeySnackbarError} setOpen={setOpenDataKeySnackbarError} />

      <RemovedKeySnackbar open={openRemovedSecretSnackbarSuccessful} setOpen={setOpenRemovedSecretSnackbarSuccessful} />

      <ImportedKeySnackbar open={openImportedSecretSnackbarSuccessful} setOpen={setOpenImportedSecretSnackbarSuccessful} />

      <PasswordPromptDialog
        text={t("insert_password")}
        action={t("forget_secret")}
        label={"Forget"}
        open={openForget}
        setOpen={setOpenForget}
        handleClose={handleCloseForget}
        password={passwordForget}
        setPassword={setPasswordForget}
        handleAction={handleForgetKey}
        invalidPassword={invalidPassword}
      />

      <PasswordPromptDialog
        text={t("insert_password")}
        action={t("import_secret")}
        label={"Import"}
        open={openPasswordImport}
        setOpen={setOpenPasswordImport}
        handleClose={handleClosePasswordImport}
        password={passwordImport}
        setPassword={setPasswordImport}
        handleAction={handleOpenMnemonicForm}
        invalidPassword={invalidPassword}
      />

      <PasswordPromptDialog
        text={t("insert_password")}
        action={t("export_secret")}
        label={"Export"}
        open={openPasswordExport}
        setOpen={setOpenPasswordExport}
        handleClose={handleClosePasswordExport}
        password={passwordExport}
        setPassword={setPasswordExport}
        handleAction={handleExportKey}
        invalidPassword={invalidPassword}
      />

      <PasswordPromptDialog
        text={t("insert_password")}
        action={t("generate_secret")}
        label={"Generate"}
        open={openPasswordGenerate}
        setOpen={setOpenPasswordGenerate}
        handleClose={handleClosePasswordGenerate}
        password={passwordGenerate}
        setPassword={setPasswordGenerate}
        handleAction={handleGenerateKey}
        invalidPassword={invalidPassword}
      />

      <MnemonicActionDialog
        action={t("import_secret")}
        label={"Import"}
        open={openMnemonicForm}
        setOpen={setOpenMnemonicForm}
        handleClose={handleCloseMnemonicForm}
        mnemonic={mnemonicImport}
        setMnemonic={setMnemonicImport}
        handleAction={handleLoadMnemonic}
        loading={loading}
      />

      <MnemonicActionDialog
        action={t("generate_secret")}
        label={"Generate"}
        open={openMnemonicGenerate}
        setOpen={setOpenMnemonicGenerate}
        handleClose={handleCloseMnemonicGenerate}
        mnemonic={mnemonicGenerate}
        setMnemonic={setMnemonicGenerate}
        handleAction={handleLoadMnemonicGenerate}
        loading={loading}
        copied={copied}
        handleCopySuccess={handleCopySuccess}
      />

      <MnemonicDialog
        open={openMnemonicExport}
        setOpen={setOpenMnemonicExport}
        handleClose={handleCloseMnemonicExport}
        mnemonic={mnemonicExport}
        setMnemonic={setMnemonicExport}
        copied={copied}
        handleCopySuccess={handleCopySuccess}
      />
    </Grid>
  );
};

export default AccountSecurity;
